import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  CreditCard,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { stripeConnectApi } from "../../services/api";

interface ConnectStatus {
  connected: boolean;
  accountId?: string;
  detailsSubmitted?: boolean;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
}

const StripeConnectSection: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await stripeConnectApi.getStatus();
      setStatus(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load Stripe connection status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleConnect = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const res = await stripeConnectApi.createAccountLink();
      window.location.href = res.data.url;
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create Stripe account link.");
      setActionLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const res = await stripeConnectApi.getDashboardLink();
      window.open(res.data.url, "_blank");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to open Stripe dashboard.");
    } finally {
      setActionLoading(false);
    }
  };

  // Determine display state
  const isConnected = status?.connected && status?.chargesEnabled;
  const isPending =
    status?.connected && status?.detailsSubmitted === false;
  const isNotConnected = !status?.connected;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </button>
        <div className="flex items-center gap-3">
          <div
            className="h-10 w-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "var(--color-primary-light)" }}
          >
            <CreditCard className="h-5 w-5" style={{ color: "var(--color-primary)" }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">
              Payment Processing
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Connect Stripe to accept client payments
            </p>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3"
        >
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-600 mt-0.5">{error}</p>
          </div>
        </motion.div>
      )}

      {/* Status Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
      >
        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-8 w-8 text-slate-400 animate-spin" />
            <p className="text-sm text-slate-500 mt-3">
              Checking connection status...
            </p>
          </div>
        )}

        {/* Not Connected State */}
        {!loading && isNotConnected && (
          <div className="flex flex-col items-center text-center py-8">
            <div className="h-16 w-16 rounded-full bg-brand-light flex items-center justify-center mb-5">
              <CreditCard className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">
              Accept Client Payments
            </h2>
            <p className="text-sm text-slate-500 max-w-md mb-8">
              Connect your Stripe account to start accepting payments from
              clients. Payments for invoices will go directly to your bank
              account.
            </p>
            <button
              onClick={handleConnect}
              disabled={actionLoading}
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3 font-bold text-white transition-colors disabled:opacity-60"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) =>
                !actionLoading &&
                (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")
              }
              onMouseLeave={(e) =>
                !actionLoading &&
                (e.currentTarget.style.backgroundColor = "var(--color-primary)")
              }
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4" />
              )}
              Connect Stripe Account
            </button>
          </div>
        )}

        {/* Pending State (details not submitted) */}
        {!loading && isPending && (
          <div className="flex flex-col items-center text-center py-8">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 rounded-full px-4 py-1.5 text-sm font-semibold mb-5">
              <AlertTriangle className="h-4 w-4" />
              Setup Incomplete
            </div>
            <h2 className="text-lg font-bold text-slate-800 mb-2">
              Complete Your Stripe Setup
            </h2>
            <p className="text-sm text-slate-500 max-w-md mb-6">
              Your Stripe account needs additional information before you can
              accept payments.
            </p>
            {status?.accountId && (
              <p className="text-xs text-slate-400 mb-6">
                Account: ...{status.accountId.slice(-8)}
              </p>
            )}
            <button
              onClick={handleConnect}
              disabled={actionLoading}
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3 font-bold text-white transition-colors disabled:opacity-60"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) =>
                !actionLoading &&
                (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")
              }
              onMouseLeave={(e) =>
                !actionLoading &&
                (e.currentTarget.style.backgroundColor = "var(--color-primary)")
              }
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="h-4 w-4" />
              )}
              Continue Setup
            </button>
          </div>
        )}

        {/* Connected State */}
        {!loading && isConnected && (
          <div className="py-4">
            {/* Connected Badge */}
            <div className="flex items-center justify-between mb-6">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 rounded-full px-4 py-1.5 text-sm font-semibold">
                <CheckCircle className="h-4 w-4" />
                Stripe Connected
              </div>
              {status?.accountId && (
                <p className="text-sm text-slate-400">
                  Account: ...{status.accountId.slice(-8)}
                </p>
              )}
            </div>

            {/* Status Indicators */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
                <div
                  className={`h-3 w-3 rounded-full flex-shrink-0 ${
                    status?.chargesEnabled ? "bg-emerald-500" : "bg-red-500"
                  }`}
                />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    Charges
                  </p>
                  <p className="text-xs text-slate-500">
                    {status?.chargesEnabled ? "Enabled" : "Disabled"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
                <div
                  className={`h-3 w-3 rounded-full flex-shrink-0 ${
                    status?.payoutsEnabled ? "bg-emerald-500" : "bg-red-500"
                  }`}
                />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    Payouts
                  </p>
                  <p className="text-xs text-slate-500">
                    {status?.payoutsEnabled ? "Enabled" : "Disabled"}
                  </p>
                </div>
              </div>
            </div>

            {/* Dashboard Button */}
            <button
              onClick={handleOpenDashboard}
              disabled={actionLoading}
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3 font-bold text-white transition-colors disabled:opacity-60"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) =>
                !actionLoading &&
                (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")
              }
              onMouseLeave={(e) =>
                !actionLoading &&
                (e.currentTarget.style.backgroundColor = "var(--color-primary)")
              }
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="h-4 w-4" />
              )}
              Open Stripe Dashboard
            </button>
          </div>
        )}

        {/* Connected but details submitted, charges not yet enabled */}
        {!loading &&
          status?.connected &&
          status?.detailsSubmitted &&
          !status?.chargesEnabled && (
            <div className="flex flex-col items-center text-center py-8">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 rounded-full px-4 py-1.5 text-sm font-semibold mb-5">
                <AlertTriangle className="h-4 w-4" />
                Verification In Progress
              </div>
              <h2 className="text-lg font-bold text-slate-800 mb-2">
                Stripe Is Reviewing Your Account
              </h2>
              <p className="text-sm text-slate-500 max-w-md mb-4">
                Your details have been submitted. Stripe is verifying your
                information. This usually takes a few minutes but can take up to
                a few days.
              </p>
              {status?.accountId && (
                <p className="text-xs text-slate-400 mb-6">
                  Account: ...{status.accountId.slice(-8)}
                </p>
              )}
              <button
                onClick={fetchStatus}
                className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
              >
                <Loader2 className="h-4 w-4" />
                Refresh Status
              </button>
            </div>
          )}
      </motion.div>
    </div>
  );
};

export default StripeConnectSection;
